import React from 'react';

import { toast, Toast, Toaster, ToastBar, Renderable } from 'react-hot-toast';

import { UnreachableFunction } from 'ui/base/types';
import { ResultKind } from 'services/base/types';
import { handleNotificationOpen } from 'services/base/ipc';
import { FCMMessage, FCMMessageKind } from 'services/base/ipc_types';
import { Host, IPangolinDomain, KnownIssue } from 'services/pangolin/types';
import { hostDisplayDeviceType, hostDisplayName, HostsService } from 'services/pangolin/hosts_service';
import { isKnownIssue, getIssueTitle } from 'services/pangolin/logic/issues/issues';
import { IssuesService } from 'services/pangolin/issues_service';

import { themedIssueKindIconImageStyles, getIssueKindIcon } from 'assets/enums/issue_kinds/issue_kinds';
import { themedDeviceTypeIconImageStyles, getDeviceTypeIcon } from 'assets/enums/device_types/device_types';

import { Theme, ThemedStyles, useStyles } from '../theme';

import Messages from './messages';
import baseStyles from './styles.module.css';

import alertSound from './assets/alert.wav';

const alertAudio: HTMLAudioElement = new Audio(alertSound);

// Only one toast per ToastID may appear.
// You can also use ToastID to dismiss toasts, e.g.,
// after saving success following a saving fail.
export enum ToastID {
  ERROR_PERMISSION_NOTIFICATIONS_MISSING = 'error-permission-notifications-missing',
  ERROR_PERMISSION_NOTIFICATIONS_BLOCKED = 'error-permission-notifications-blocked',
  ERROR_ACTION_PANGOLIN_OFFLINE = 'error-saving-pangolin-offline',
  ERROR_ACTION_PLAN_REQUIRED = 'error-saving-plan-required',
  ERROR_ACTION_GENERIC = 'error-saving-generic',
  ERROR_QUERY_PANGOLIN_OFFLINE = 'error-query-pangolin-offline',
  ERROR_QUERY_PLAN_REQUIRED = 'error-query-plan-required',
  ERROR_QUERY_GENERIC = 'error-query-generic',
}

export const defaultToastIds: readonly string[] = Object.values(ToastID);

export const isDefaultToastId = (id: string): id is ToastID => {
  return defaultToastIds.includes(id);
};

export const defaultMessage = (id: ToastID): string => {
  switch (id) {
    case ToastID.ERROR_PERMISSION_NOTIFICATIONS_MISSING:
      return Messages.errorPermissionNotificationsMissing();
    case ToastID.ERROR_PERMISSION_NOTIFICATIONS_BLOCKED:
      return Messages.errorPermissionNotificationsBlocked();
    case ToastID.ERROR_ACTION_PANGOLIN_OFFLINE:
      return Messages.errorActionPangolinOffline();
    case ToastID.ERROR_ACTION_PLAN_REQUIRED:
      return Messages.errorActionPlanRequired();
    case ToastID.ERROR_ACTION_GENERIC:
      return Messages.errorActionGeneric();
    case ToastID.ERROR_QUERY_PANGOLIN_OFFLINE:
      return Messages.errorQueryPangolinOffline();
    case ToastID.ERROR_QUERY_PLAN_REQUIRED:
      return Messages.errorQueryPlanRequired();
    case ToastID.ERROR_QUERY_GENERIC:
      return Messages.errorQueryGeneric();
    default:
      return UnreachableFunction(id);
  }
};

export const defaultAction = (id: ToastID) => {
  switch (id) {
    case ToastID.ERROR_PERMISSION_NOTIFICATIONS_MISSING: {
      const notifications = window.WebNotifications;
      if (notifications) {
        notifications.requestNotificationPermission().then(granted => {
          if (granted) notifications.initialize();
        });
      }
      return;
    }
    case ToastID.ERROR_PERMISSION_NOTIFICATIONS_BLOCKED: {
      // do nothing for now
      return;
    }
    case ToastID.ERROR_ACTION_PANGOLIN_OFFLINE:
    case ToastID.ERROR_ACTION_PLAN_REQUIRED:
    case ToastID.ERROR_ACTION_GENERIC:
    case ToastID.ERROR_QUERY_PANGOLIN_OFFLINE:
    case ToastID.ERROR_QUERY_PLAN_REQUIRED:
    case ToastID.ERROR_QUERY_GENERIC: {
      return;
    }
    default: {
      return UnreachableFunction(id);
    }
  }
};

export const defaultToast = (id: ToastID) => {
  const message: string = defaultMessage(id);
  switch (id) {
    case ToastID.ERROR_PERMISSION_NOTIFICATIONS_MISSING:
    case ToastID.ERROR_PERMISSION_NOTIFICATIONS_BLOCKED:
      return toast.error(message, { id, duration: Infinity });
    case ToastID.ERROR_ACTION_PANGOLIN_OFFLINE:
    case ToastID.ERROR_ACTION_PLAN_REQUIRED:
    case ToastID.ERROR_ACTION_GENERIC:
    case ToastID.ERROR_QUERY_PANGOLIN_OFFLINE:
    case ToastID.ERROR_QUERY_PLAN_REQUIRED:
    case ToastID.ERROR_QUERY_GENERIC:
      return toast.error(message, { id });
    default:
      return UnreachableFunction(id);
  }
};

export type ToastableNotification = Extract<FCMMessage, { kind: FCMMessageKind.HOST_CONNECTED | FCMMessageKind.NEW_INCIDENT }>;

export const performSaving = async<T, >(action_: () => Promise<T>): Promise<T> => {
  return await toast.promise(action_, {
    loading: Messages.saveLoading(),
    success: Messages.saveSuccess(),
    error: Messages.saveError(),
  });
};

type ToastStyles = {
  maxWidth: string;
  borderRadius: string;
  background: string;
  color: string;
  cursor: string;
  boxShadow: string;
};

type ToastContainerStyles = {
  toastContainer: string;
  toastIcon: string;
  toastText: string;
  toastClose: string;
};

const toastThemedStyles: ThemedStyles<ToastStyles> = {
  [Theme.DAYLIGHT]: {
    maxWidth: '280px',
    borderRadius: '20px',
    background: 'rgba(204,204,204,0.9)',
    color: '#000',
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },
  [Theme.MIDNIGHT]: {
    maxWidth: '280px',
    borderRadius: '20px',
    background: 'rgba(51,51,51,0.9)',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.45)',
  },
};

const toastContainerThemedStyles: ThemedStyles<ToastContainerStyles> = {
  [Theme.DAYLIGHT]: {
    toastContainer: baseStyles.toastContainer,
    toastIcon: baseStyles.toastIcon,
    toastText: baseStyles.toastText,
    toastClose: baseStyles.toastCloseDaylight,
  },
  [Theme.MIDNIGHT]: {
    toastContainer: baseStyles.toastContainer,
    toastIcon: baseStyles.toastIcon,
    toastText: baseStyles.toastText,
    toastClose: baseStyles.toastCloseMidnight,
  },
};

type PlainToastContainerProps = {
  t: Toast;
  icon: Renderable;
  message: Renderable;
};
export const PlainToastContainer = ({ icon, message }: PlainToastContainerProps) => {
  const styles = useStyles(toastContainerThemedStyles);
  return (
    <div className={styles.toastContainer}>
      {icon} {message}
    </div>
  );
};

type DismissibleToastContainerProps = {
  t: Toast;
  icon: Renderable;
  message: Renderable;
};
export const DismissibleToastContainer = ({ t, icon, message }: DismissibleToastContainerProps) => {
  const style = useStyles(toastContainerThemedStyles);
  const onClick = React.useCallback(() => {
    toast.dismiss(t.id);
    if (isDefaultToastId(t.id)) {
      defaultAction(t.id);
    }
  }, [t.id]);
  return (
    <div className={style.toastContainer} onClick={onClick}>
      {icon} {message}
    </div>
  );
};

type NotificationHostConnectedToastContainerProps = {
  t: Toast; domain: IPangolinDomain; host: Host;
  notification: ToastableNotification;
};
export const NotificationHostConnectedToastContainer = ({ t, host, notification }: NotificationHostConnectedToastContainerProps) => {
  const styles = useStyles(toastContainerThemedStyles);
  const images = useStyles(themedDeviceTypeIconImageStyles);

  const type = hostDisplayDeviceType(host);
  const name = hostDisplayName(host) ?? host.mac ?? host.ip;
  const icon = getDeviceTypeIcon(images, type);
  const title = Messages.hostConnected(name);

  React.useEffect(() => { alertAudio.play().catch(() => { /* ignore */ }); }, []);

  const onOpen = React.useCallback(() => {
    toast.dismiss(t.id);
    handleNotificationOpen(notification);
  }, [t.id, notification]);

  const onClose = React.useCallback(() => {
    toast.dismiss(t.id);
  }, [t.id]);

  return (
    <div className={styles.toastContainer}>
      <img className={styles.toastIcon} onClick={onOpen} src={icon} alt='Host Icon' />
      <div className={styles.toastText} onClick={onOpen}>{title}</div>
      <div className={styles.toastClose} onClick={onClose}>✕</div>
    </div>
  );
};

type NotificationNewIncidentToastContainerProps = {
  t: Toast; domain: IPangolinDomain; issue: KnownIssue;
  notification: ToastableNotification;
};
export const NotificationNewIncidentToastContainer = ({ t, domain, issue, notification }: NotificationNewIncidentToastContainerProps) => {
  const styles = useStyles(toastContainerThemedStyles);
  const images = useStyles(themedIssueKindIconImageStyles);

  const icon = getIssueKindIcon(images, issue.kind);
  const title = getIssueTitle(domain, issue);

  const onOpen = React.useCallback(() => {
    toast.dismiss(t.id);
    handleNotificationOpen(notification);
  }, [t.id, notification]);

  const onClose = React.useCallback(() => {
    toast.dismiss(t.id);
  }, [t.id]);

  React.useEffect(() => { alertAudio.play().catch(() => { /* ignore */ }); }, []);

  return (
    <div className={styles.toastContainer}>
      <img className={styles.toastIcon} onClick={onOpen} src={icon} alt='Issue Icon' />
      <div className={styles.toastText} onClick={onOpen}>{title}</div>
      <div className={styles.toastClose} onClick={onClose}>✕</div>
    </div>
  );
};

export const DefaultToaster = () => {
  const style = useStyles(toastThemedStyles);
  return (
    <Toaster reverseOrder={true} position='top-center' toastOptions={{ style }}>
      {(t: Toast) => (
        <ToastBar toast={t}>
          {({ icon, message }) => {
            switch (t.type) {
              case 'success': return <DismissibleToastContainer t={t} icon={icon} message={message} />;
              case 'error': return <DismissibleToastContainer t={t} icon={icon} message={message} />;
              case 'loading': return <PlainToastContainer t={t} icon={icon} message={message} />;
              case 'blank': return <PlainToastContainer t={t} icon={icon} message={message} />;
              case 'custom': return <PlainToastContainer t={t} icon={icon} message={message} />;
              default: return <PlainToastContainer t={t} icon={icon} message={message} />;
            }
          }}
        </ToastBar>
      )}
    </Toaster>
  );
};

export const notificationToast = async(domain: IPangolinDomain, notification: ToastableNotification) => {
  switch (notification.kind) {
    case FCMMessageKind.HOST_CONNECTED: {
      const host = await HostsService.fetch(domain, notification.hostID, false);
      if (host) {
        return toast((t) => (
          <NotificationHostConnectedToastContainer
            t={t} domain={domain} host={host}
            notification={notification}
          />
        ), { duration: 10000 });
      }
      return;
    }
    case FCMMessageKind.NEW_INCIDENT: {
      const fetch = await IssuesService.fetchItem(domain, notification.issueID, false);
      const issue = fetch.kind === ResultKind.OK ? fetch.issue : undefined;
      if (issue && isKnownIssue(issue)) {
        return toast((t) => (
          <NotificationNewIncidentToastContainer
            t={t} domain={domain} issue={issue}
            notification={notification}
          />
        ), { duration: 10000 });
      }
      return;
    }
    default:
      return UnreachableFunction(notification);
  }
};
